import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Handshake, Eye, ShieldCheck, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from './SEO';

const PROPOSITIONS = [
  {
    icon: Handshake,
    title: 'Your relationship, preserved',
    body: 'MyInvestFolio is an infrastructure layer, not an advisory firm. We never displace, contact or solicit your clients. The client relationship is, and remains, yours.',
  },
  {
    icon: Eye,
    title: 'A complete view your client can trust',
    body: 'Your client sees their entire portfolio in one private, encrypted place — across institutions, currencies, holding entities and jurisdictions. They come to advisory conversations better-prepared, with cleaner data.',
  },
  {
    icon: ShieldCheck,
    title: 'No commissions. No data sales. No conflicts.',
    body: 'We do not earn from any broker, fund, bank or platform. We do not sell client data. We do not use external AI or analytics on portfolio content. Independence is structural, not a slogan.',
  },
  {
    icon: Award,
    title: 'The Partner Rate',
    body: 'Clients introduced through trusted professionals receive a 20% reduction on the standard subscription rate. The discount is applied automatically and persists for the life of the subscription.',
  },
];

const ForAdvisorsPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-black text-white">
      <SEO
        title="For Trusted Professionals"
        description="A private channel for wealth advisors, accountants, lawyers, financial planners and other trusted professionals. Introduce a trusted client to MyInvestFolio — independent, encrypted portfolio infrastructure that preserves your professional relationship."
        path="/for-advisors"
      />
      <header className="border-b border-gray-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
            <span className="text-xl font-bold">My<span className="text-green-600">Invest</span><span className="text-blue-600">Folio</span></span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 max-w-4xl">
        <div className="text-center mb-14">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-emerald-600">For Trusted Professionals</h1>
          <div className="w-24 h-1 mx-auto bg-linear-to-r from-emerald-500 to-blue-500 mb-6"></div>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
            For wealth advisors, accountants, lawyers, financial planners and other trusted professionals — give your clients private portfolio visibility without compromising your professional mandate.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
          {PROPOSITIONS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="bg-gray-800 rounded-xl p-6 border border-gray-700" data-testid={`advisor-prop-${p.title.replace(/\s+/g, '-').toLowerCase()}`}>
                <Icon className="w-8 h-8 text-emerald-400 mb-3" />
                <h2 className="text-xl font-semibold mb-2 text-emerald-400">{p.title}</h2>
                <p className="text-base text-gray-300 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>

        {/* Trust strip */}
        <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-6 mb-14">
          <h3 className="text-lg font-semibold text-gray-200 mb-3">How it works</h3>
          <ol className="space-y-2 text-base text-gray-300 list-decimal list-inside">
            <li>You submit your client's name and email through the trusted introductions form below.</li>
            <li>We send your client a private invitation referencing your introduction.</li>
            <li>Your client signs up at the Partner Rate (20% off). You receive a confirmation once activated.</li>
            <li>You maintain full visibility of every client you've introduced through your Trusted Professional Portal.</li>
          </ol>
        </div>

        <div className="text-center bg-emerald-900/30 border border-emerald-700/50 rounded-xl p-10">
          <h2 className="text-2xl font-bold text-white mb-3">Introduce a Trusted Client</h2>
          <p className="text-base text-gray-300 mb-6 max-w-xl mx-auto">
            Trusted introductions are reviewed personally. Your clients will never receive unsolicited contact.
          </p>
          <Button
            onClick={() => navigate('/advisor-portal')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-3 rounded-lg shadow-lg"
            data-testid="advisor-introduce-client-cta"
          >
            Introduce a Trusted Client →
          </Button>
          <p className="text-xs text-gray-400 mt-6">
            Already a partner?{' '}
            <button onClick={() => navigate('/advisor-portal')} className="text-emerald-400 underline hover:text-emerald-300">
              Sign in to the Trusted Professional Portal
            </button>
          </p>
        </div>
      </main>
    </div>
  );
};

export default ForAdvisorsPage;
